/**
 * Concept Levels under a Unit — shared by the Reference > Design > Concepts branch
 * (DesignSection.jsx) and the DAPC Drawings milestone Design node
 * (MilestoneSections.jsx). Self-contained: fetches/mutates its own level list via
 * services/design.js and mirrors every change into ctx.levelCache so the vanilla
 * outline engine's breadcrumb/label-resolution code (which walks that cache
 * synchronously) stays correct without a second cache.
 *
 * Renders in two modes:
 *   - tree mode (default): full "Levels" category row (triangle/bullet/⋯) wrapping
 *     the level rows — used inline in the Concept/Unit tree and in the concept-detail
 *     / unit-detail zoom pages (those aren't pure "sections", so they keep the header).
 *   - zoomMode: children only, no header — used for the dedicated
 *     sec:unit:<unitId>:levels zoom, where ZoomTitle supplies the title/count/menu.
 *     "Add Level" in that menu reaches this component via the 'open-add-level'
 *     CustomEvent (detail: { unitId }), matching the open-add-sheets pattern.
 */
import { useState, useEffect, useRef } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { confirmTypeDialog } from './Dialog.jsx'
import { AddLevelModal, LevelRenameModal } from './ConceptUnitModals.jsx'
import { ConceptLevelAreaMetrics } from './ConceptLevelAreaFields.jsx'
import ConceptRoomsSection from './ConceptRoomsSection.jsx'
import {
  getConceptLevels, createConceptLevel, updateConceptLevel,
  deleteConceptLevelCascade, getLevelRooms,
} from '../services/design.js'

function emptyRow(message) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'rgba(7,39,45,0.4)', fontStyle: 'italic' }}>{message}</span>
        </div>
      </div>
    </div>
  )
}

function recomputeTotals(fields) {
  return {
    ...fields,
    'Total Living Area':             (fields['New Living Area'] || 0) + (fields['Existing Living Area'] || 0),
    'Total Garage Area':             (fields['New Garage Area'] || 0) + (fields['Existing Garage Area'] || 0),
    'Total Elevated Decks Area':     (fields['New Elevated Decks Area'] || 0) + (fields['Existing Elevated Decks Area'] || 0),
    'Total Mechanical Storage Area': (fields['New Mechanical Storage Area'] || 0) + (fields['Existing Mechanical Storage Area'] || 0),
  }
}

export function ConceptLevelRow({ level, unitId, conceptId, onSave, onRename, onDelete, hideAreaMetrics }) {
  const colKey = 'level:' + level.id
  const isCollapsed = wf.collapsed[colKey] !== false
  const levelName = level.fields['Name'] || 'Level'
  const menu = useActionMenu()
  const [renameOpen, setRenameOpen] = useState(false)

  async function handleDelete() {
    menu.close()
    const rooms = await getLevelRooms(level.id)
    const ok = await confirmTypeDialog({
      title: 'Delete level?',
      message: `This will permanently delete "${levelName}" and ${rooms.length} room(s) under it. This cannot be undone.`,
    })
    if (!ok) return
    await onDelete(level.id)
  }

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={colKey}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={colKey}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--item" title="Focus on level"
          data-fn="zoomTo" data-level="level-detail" data-levelid={level.id} data-unitid={unitId} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{levelName}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); setRenameOpen(true) }}>Edit name</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={handleDelete} danger>Delete level</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <ConceptRoomsSection levelId={level.id} conceptId={conceptId} levelName={levelName} />
          <ConceptLevelAreaMetrics level={level} onSave={onSave} hideAreaMetrics={hideAreaMetrics} />
        </div>
      )}
      {renameOpen && (
        <LevelRenameModal
          initial={levelName}
          onSave={name => onRename(level.id, name)}
          onClose={() => setRenameOpen(false)}
        />
      )}
    </div>
  )
}

export default function ConceptLevelsSection({ unitId, conceptId, zoomMode, hideAreaMetrics }) {
  const [levels, setLevels] = useState(null)
  const loadedRef = useRef(false)
  const menu = useActionMenu()
  const [addOpen, setAddOpen] = useState(false)
  const key = 'sec:unit:' + unitId + ':levels'
  const isCollapsed = zoomMode ? false : (wf.collapsed[key] !== false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!unitId || loadedRef.current) return
    loadedRef.current = true
    getConceptLevels(unitId)
      .then(records => { const r = records || []; setLevels(r); ctx.levelCache[unitId] = r; ctx.render() })
      .catch(() => setLevels([]))
  }, [unitId])

  // zoomMode: "Add Level" lives in ZoomTitle's ⋯ menu — bridged via CustomEvent.
  useEffect(() => {
    if (!zoomMode) return
    function onOpenAdd(e) { if (e.detail?.unitId === unitId) setAddOpen(true) }
    window.addEventListener('open-add-level', onOpenAdd)
    return () => window.removeEventListener('open-add-level', onOpenAdd)
  }, [zoomMode, unitId])

  async function handleAdd(fields) {
    await createConceptLevel(unitId, fields)
    const records = await getConceptLevels(unitId)
    setLevels(records)
    ctx.levelCache[unitId] = records
    ctx.render()
  }

  async function handleRename(levelId, name) {
    await updateConceptLevel(levelId, { Name: name })
    setLevels(prev => {
      const next = (prev || []).map(l => l.id === levelId ? { ...l, fields: { ...l.fields, Name: name } } : l)
      ctx.levelCache[unitId] = next
      return next
    })
    ctx.render()
  }

  async function handleSave(levelId, fields) {
    await updateConceptLevel(levelId, fields)
    setLevels(prev => {
      const next = (prev || []).map(l => l.id === levelId ? { ...l, fields: recomputeTotals({ ...l.fields, ...fields }) } : l)
      ctx.levelCache[unitId] = next
      return next
    })
  }

  async function handleDelete(levelId) {
    await deleteConceptLevelCascade(levelId)
    setLevels(prev => {
      const next = (prev || []).filter(l => l.id !== levelId)
      ctx.levelCache[unitId] = next
      return next
    })
    ctx.render()
  }

  const count = Array.isArray(levels) ? levels.length : null
  const nextOrder = Array.isArray(levels) ? levels.length + 1 : 1

  const content = (
    <>
      {!levels && <div className="wf-loading">Loading…</div>}
      {Array.isArray(levels) && levels.length === 0 && emptyRow('No levels yet')}
      {Array.isArray(levels) && levels.map(level => (
        <ConceptLevelRow
          key={level.id}
          level={level}
          unitId={unitId}
          conceptId={conceptId}
          onSave={handleSave}
          onRename={handleRename}
          onDelete={handleDelete}
          hideAreaMetrics={hideAreaMetrics}
        />
      ))}
    </>
  )

  const addModal = addOpen && (
    <AddLevelModal
      defaultName={'Level ' + nextOrder}
      defaultOrder={nextOrder}
      onSave={handleAdd}
      onClose={() => setAddOpen(false)}
    />
  )

  if (zoomMode) {
    return (
      <div className="wf-list">
        {content}
        {addModal}
      </div>
    )
  }

  return (
    <div className={`wf-node wf-node--cat${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--cat" title="Focus on Levels"
          data-fn="zoomTo" data-level="unit-levels" data-unitid={unitId} data-conceptid={conceptId} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">Levels</span>
          {count != null && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{count}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); setAddOpen(true) }}>Add Level</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children">{content}</div>}
      {addModal}
    </div>
  )
}
